class CustomerRegistrationForm extends HTMLElement {
  constructor() {
    super()
    this.form = this.querySelector('form')
    this.form.addEventListener('submit', this.handleSubmit.bind(this))
    this.acceptTermsCheckbox = this.querySelector('input[id="AcceptTerms"]')
    this.generalError = this.querySelector('div#GeneralError')
    this.successMessage = this.querySelector('div#SuccessMessage')
    this.vatPayerSelector = this.querySelector('select[name="vatPayer"]')
    this.submitText = this.querySelector('button[type="submit"] div.text')
    this.submitSpinner = this.querySelector('button[type="submit"] div.loading-overlay')
    this.vatInput = this.querySelector('input[name="vatId"]')
    this.countrySelect = this.querySelector('select[name="billingCountry"]')
    this.viesStatus = this.querySelector('.vies-status')
    this.viesSuggestion = this.querySelector('.vies-suggestion')
    this.viesCache = {}
    this.viesSuggestionData = null
  }

  connectedCallback() {
    this.acceptTermsCheckbox.addEventListener('change', this.handleAcceptTermsCheckboxChange.bind(this))
    const onVatChange = () => {
      clearTimeout(this.viesTimer)
      this.viesTimer = setTimeout(() => this.checkVies(), 500)
    }
    this.vatInput.addEventListener('input', onVatChange)
    this.countrySelect.addEventListener('change', onVatChange)
    this.querySelector('.vies-suggestion__fill').addEventListener('click', this.applyViesSuggestion.bind(this))
    this.querySelector('.vies-suggestion__dismiss').addEventListener('click', () => this.viesSuggestion.classList.add('hidden'))
  }

  // VIES uses EL for Greece
  normalizedVatNumber() {
    const value = this.vatInput.value.replace(/\s+/g, '').toUpperCase()
    if (!value || /^[A-Z]{2}/.test(value)) return value
    const country = this.countrySelect.value
    return (country === 'GR' ? 'EL' : country) + value
  }

  async checkVies() {
    const vatNumber = this.normalizedVatNumber()
    this.viesSuggestion.classList.add('hidden')
    if (!/^[A-Z]{2}[A-Z0-9]{2,12}$/.test(vatNumber)) {
      this.viesStatus.classList.add('hidden')
      return
    }

    this.setViesStatus('', this.viesStatus.dataset.verifying)
    let data = this.viesCache[vatNumber]
    if (!data) {
      try {
        const response = await fetch(window.routes.validate_vat_url, {
          ...fetchConfig(),
          body: JSON.stringify({ vatNumber }),
          signal: AbortSignal.timeout(15000),
        })
        data = await response.json()
        if (data.valid !== null) this.viesCache[vatNumber] = data
      } catch (e) {
        data = { valid: null }
      }
    }
    if (vatNumber !== this.normalizedVatNumber()) return

    if (data.valid === true) {
      this.setViesStatus('valid', '\u2713 ' + this.viesStatus.dataset.valid)
      this.showViesSuggestion(data)
    } else if (data.valid === false) {
      this.setViesStatus('invalid', '\u2717 ' + this.viesStatus.dataset.invalid)
    } else {
      this.setViesStatus('invalid', this.viesStatus.dataset.unavailable)
    }
  }

  setViesStatus(state, message) {
    this.viesStatus.className = 'vies-status' + (state ? ' vies-status--' + state : '')
    this.viesStatus.textContent = message
  }

  showViesSuggestion(data) {
    const address = data.address || {}
    if (!data.company_name && !address.street) return
    this.viesSuggestionData = data
    this.viesSuggestion.querySelector('.vies-suggestion__company').textContent = data.company_name || ''
    this.viesSuggestion.querySelector('.vies-suggestion__address').textContent =
      [address.street, [address.zip, address.city].filter(Boolean).join(' ')].filter(Boolean).join(', ')
    this.viesSuggestion.classList.remove('hidden')
  }

  applyViesSuggestion() {
    const { company_name, address = {} } = this.viesSuggestionData || {}
    const fill = (name, value) => { if (value) this.form.elements[name].value = value }
    fill('companyName', company_name)
    fill('billingStreet', address.street)
    fill('billingCity', address.city)
    fill('billingZip', address.zip)
    this.viesSuggestion.classList.add('hidden')
  }

  disconnectedCallback() {
    this.acceptTermsCheckbox.removeEventListener('change', this.handleAcceptTermsCheckboxChange.bind(this))
  }

  async handleSubmit(e) {
    try {
      e.preventDefault()
      this.submitText.classList.add('hidden')
      this.submitSpinner.classList.remove('hidden')
      const formData = new FormData(this.form)
      const data = Object.fromEntries(formData.entries())

      const formattedData = {
        customerId: Number(data.customerId),
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
        ico: data.businessId,
        dic: data.vatId,
        type: 'Customer',
        billingAddress: {
          firstName: data.firstName,
          lastName: data.lastName,
          company: data.companyName,
          address: data.billingStreet,
          city: data.billingCity,
          countryCode: data.billingCountry,
          phone: data.phone,
          zip: data.billingZip,
        }
      }
      const response = await fetch(
        window.routes.complete_profile_url,
        { ...fetchConfig(), ...{ body: JSON.stringify(formattedData) } },
      )
      const responseData = await response.json()

      if (responseData.success || responseData.message === 'profileComplete') {
        this.successMessage.classList.remove('hidden')
        this.generalError.classList.add('hidden')
        this.form.classList.add('hidden')
        window.location.href = this.dataset.successUrl
        return
      }
      this.generalError.classList.remove('hidden')
      this.successMessage.classList.add('hidden')
    } catch(e) {
      console.error(e)
      this.successMessage.classList.add('hidden')
      this.generalError.classList.remove('hidden')
    }
    this.submitText.classList.remove('hidden')
    this.submitSpinner.classList.add('hidden')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  handleAcceptTermsCheckboxChange() {
    if (this.acceptTermsCheckbox.checked) {
      this.querySelector('button[type="submit"]').removeAttribute('disabled')
    } else {
      this.querySelector('button[type="submit"]').setAttribute('disabled', 'disabled')
    }
  }
}

customElements.define('customer-registration-form', CustomerRegistrationForm)
